import { AxiosResponse } from "axios"
import { addDays, format, parseISO } from "date-fns"
import { apiInstance } from "../base"
import {
    IListRestaurantMenuOrdersQuery,
    IListRestaurantMenuOrdersResponse,
    IRestaurantMenuOrderCard,
    IRestaurantMenuOrdersPeriodQuery,
    IRestaurantMenuOrderSummaryQuery,
    IRestaurantMenuOrderSummaryResponse,
    IRestaurantMenuOrderTimeSeriesQuery,
    IRestaurantMenuOrderTimeSeriesResponse,
} from "./types"

const appendArray = (params: Record<string, string | number>, key: string, values?: string[]) => {
    if (values?.length) {
        params[key] = values.join(",")
    }
}

// PeriodPicker emits inclusive calendar days (yyyy-MM-dd). The satellite query is half-open —
// `created_at >= period_from AND created_at < period_to` (order_analytics_repo.go @ N4). Sending the
// inclusive end day unchanged would drop that entire day (and make a single-day period empty), so we
// shift the end to the next-day midnight boundary before serializing.
const toExclusiveEndDay = (periodTo?: string): string | undefined => {
    if (!periodTo) return undefined
    const parsed = parseISO(periodTo)
    if (Number.isNaN(parsed.getTime())) return periodTo

    return format(addDays(parsed, 1), "yyyy-MM-dd")
}

const buildPeriodParams = (query: IRestaurantMenuOrdersPeriodQuery): Record<string, string | number> => {
    const params: Record<string, string | number> = {}

    if (query.periodFrom) params.period_from = query.periodFrom
    const periodTo = toExclusiveEndDay(query.periodTo)
    if (periodTo) params.period_to = periodTo
    appendArray(params, "menu_ids", query.menuIds)
    appendArray(params, "statuses", query.statuses)

    return params
}

export const listOrders = (
    query: IListRestaurantMenuOrdersQuery
): Promise<AxiosResponse<IListRestaurantMenuOrdersResponse>> => {
    const params = buildPeriodParams(query)
    appendArray(params, "table_ids", query.tableIds)
    if (query.page) params.page = query.page
    if (query.pageSize) params.page_size = query.pageSize

    return apiInstance.handler.get<IListRestaurantMenuOrdersResponse>("/restaurant-menus/orders", { params })
}

export const getOrdersSummary = (
    query: IRestaurantMenuOrderSummaryQuery
): Promise<AxiosResponse<IRestaurantMenuOrderSummaryResponse>> => {
    const params = buildPeriodParams(query)
    appendArray(params, "table_ids", query.tableIds)

    return apiInstance.handler.get<IRestaurantMenuOrderSummaryResponse>("/restaurant-menus/orders/summary", { params })
}

export const getOrdersTimeSeries = (
    query: IRestaurantMenuOrderTimeSeriesQuery
): Promise<AxiosResponse<IRestaurantMenuOrderTimeSeriesResponse>> => {
    const params = buildPeriodParams(query)
    if (query.interval) params.interval = query.interval

    return apiInstance.handler.get<IRestaurantMenuOrderTimeSeriesResponse>(
        "/restaurant-menus/orders/time-series",
        { params }
    )
}

export const getOrder = (orderId: string): Promise<AxiosResponse<IRestaurantMenuOrderCard>> => {
    return apiInstance.handler.get<IRestaurantMenuOrderCard>(
        `/restaurant-menus/orders/${encodeURIComponent(orderId)}`,
        { timeout: 70_000 }
    )
}
